import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { X, MagnifyingGlass, Trash, Plus, ArrowRight } from "@phosphor-icons/react";

export default function SkuAliasModal({ onClose, initialSku = "" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [alias, setAlias] = useState("");
  const [skuQ, setSkuQ] = useState(initialSku);
  const [skuRes, setSkuRes] = useState([]);
  const [picked, setPicked] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/warehouse/sku-aliases?q=${encodeURIComponent(q)}&limit=300`);
    setRows(r.data.items); setTotal(r.data.total);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (initialSku) api.get(`/warehouse/skus?q=${encodeURIComponent(initialSku)}&limit=5`).then((r) => {
      const m = (r.data.items || []).find((s) => s.sku === initialSku); if (m) setPicked(m);
    }).catch(() => {});
  }, [initialSku]);

  const searchSku = async (v) => {
    setSkuQ(v); setPicked(null);
    if (v.length < 2) { setSkuRes([]); return; }
    const r = await api.get(`/warehouse/skus?q=${encodeURIComponent(v)}&limit=8`);
    setSkuRes(r.data.items || []);
  };

  const add = async () => {
    if (!alias.trim() || !picked) { setError("Enter the Tally / external code and pick a POS SKU."); return; }
    setSaving(true); setError("");
    try {
      await api.post("/warehouse/sku-aliases", { alias: alias.trim(), sku: picked.sku });
      setAlias(""); load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove mapping "${r.alias}" → ${r.sku}?`)) return;
    await api.delete(`/warehouse/sku-aliases/${r.id}`); load();
  };

  const inputCls = "h-10 px-3 border border-slate-300 rounded-md text-sm";

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="alias-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Tally Code → SKU Mapping</div>
            <div className="text-xs text-slate-500">Map codes printed on Tally challans to your POS SKUs. PDF imports auto-resolve these instead of creating duplicate SKUs. Admin only.</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>

        <div className="p-5 border-b border-slate-200 bg-slate-50">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-2 items-start">
            <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Tally / external code (e.g. MaternityBag_Cozycarry-Black)" data-testid="alias-input" className={`${inputCls} font-mono-num w-full`} />
            <div className="h-10 flex items-center justify-center text-slate-400"><ArrowRight size={16} /></div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-3 text-slate-400" />
              <input value={picked ? `${picked.sku} — ${picked.name}` : skuQ} onChange={(e) => searchSku(e.target.value)} placeholder="Search POS SKU…" data-testid="alias-sku-input" className={`${inputCls} pl-8 w-full ${picked ? "border-emerald-400 bg-emerald-50" : ""}`} />
              {!picked && skuRes.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded-md shadow-lg max-h-56 overflow-y-auto divide-y divide-slate-100">
                  {skuRes.map((s) => (
                    <button key={s.sku} onClick={() => { setPicked(s); setSkuRes([]); }} data-testid={`alias-sku-pick-${s.sku}`} className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center gap-2">
                      {s.thumbnail ? <img src={s.thumbnail} alt="" className="w-7 h-7 rounded-sm object-cover" /> : <div className="w-7 h-7 rounded-sm bg-slate-100" />}
                      <div className="min-w-0"><div className="text-xs font-semibold truncate">{s.name}</div><div className="text-[10px] text-slate-500 font-mono-num">{s.sku}</div></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={add} disabled={saving} data-testid="alias-add-btn" className="h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md inline-flex items-center gap-1 disabled:opacity-50"><Plus size={14} weight="bold" />Map</button>
          </div>
          {error && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="alias-error">{error}</div>}
        </div>

        <div className="px-5 py-3 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter mappings…" data-testid="alias-filter" className={`${inputCls} pl-8 w-full h-9`} />
          </div>
          <div className="text-xs text-slate-500 font-mono-num ml-auto" data-testid="alias-total">{total} mapping(s)</div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          <table className="w-full text-sm" data-testid="alias-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="text-left px-3 py-2 font-semibold">Tally / external code</th><th className="text-left px-3 py-2 font-semibold">POS SKU</th><th className="text-left px-3 py-2 font-semibold">Source</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-400">No mappings yet. Add one above, or they are saved automatically when you map lines during a PDF import.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`alias-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono-num text-slate-900">{r.alias}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {r.thumbnail ? <img src={r.thumbnail} alt="" className="w-7 h-7 rounded-sm object-cover" /> : <div className="w-7 h-7 rounded-sm bg-slate-100" />}
                      <div className="min-w-0"><div className="font-mono-num text-xs font-semibold">{r.sku}</div><div className="text-[11px] text-slate-500 truncate max-w-[260px]">{r.sku_name}</div></div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500">{r.source === "pdf_import" ? "PDF import" : "Manual"}</td>
                  <td className="px-3 py-2 text-right"><button onClick={() => remove(r)} data-testid={`alias-del-${r.id}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
